import { FC } from 'react';
import {
  LocalizationProvider,
  DatePicker,
  DatePickerProps,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { Controller } from 'react-hook-form';
import FieldGridContainer from '../fieldGridContainer';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

export type ControlledDateFieldProps<TDate extends Dayjs> =
  DatePickerProps<TDate> & {
    control?: any;
    isGridItem?: boolean;
    label: string;
    id: string;
    name: string;
    helperText?: string;
    errorMessage?: string;
    required?: boolean;
  };

const ControlledDateField: FC<ControlledDateFieldProps<any>> = (
  props: ControlledDateFieldProps<any>
) => {
  const {
    control,
    isGridItem = true,
    label,
    id,
    name,
    helperText = '',
    errorMessage,
    ...otherProps
  } = props;

  if (!control) {
    const { value, onChange, ...restProps } = otherProps;
    const dayjsValue = value
      ? typeof value === 'string' || value instanceof Date
        ? dayjs(value)
        : value
      : null;

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          data-name={id}
          data-testid={`date-field-${id}`}
          value={dayjsValue}
          onChange={onChange}
          slotProps={{
            textField: {
              helperText:
                errorMessage ??
                (helperText ? `${helperText} [MM/DD/YYYY]` : '[MM/DD/YYYY]'),
              id,
              error: !!errorMessage,
              size: 'small',
              sx: {
                '& .MuiInputBase-root': {
                  minHeight: {
                    xs: '44px',
                    sm: '40px',
                  },
                  fontSize: {
                    xs: '16px', // Prevents zoom on iOS
                    sm: '14px',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: {
                    xs: '0.875rem',
                    sm: '1rem',
                  },
                },
              },
            },
          }}
          label={label}
          {...restProps}
        />
      </LocalizationProvider>
    );
  }

  if (isGridItem) {
    return (
      <FieldGridContainer>
        <Controller
          name={name ?? ''}
          control={control}
          render={({ field }) => {
            const { value } = field;
            const dayjsValue = value
              ? typeof value === 'string' || value instanceof Date
                ? dayjs(value)
                : value
              : null;
            const {
              onChange: otherOnChange,
              value: otherValue,
              ...restProps
            } = otherProps;

            return (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  data-name={id}
                  data-testid={`date-field-${id}`}
                  value={dayjsValue}
                  onChange={(date: Dayjs | null) => {
                    field.onChange(date ? date.format('YYYY-MM-DD') : '');
                  }}
                  slotProps={{
                    textField: {
                      helperText:
                        errorMessage ??
                        (helperText
                          ? `${helperText} [MM/DD/YYYY]`
                          : '[MM/DD/YYYY]'),
                      id,
                      error: !!errorMessage,
                      size: 'small',
                      sx: {
                        '& .MuiInputBase-root': {
                          minHeight: {
                            xs: '44px',
                            sm: '40px',
                          },
                          fontSize: {
                            xs: '16px', // Prevents zoom on iOS
                            sm: '14px',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                          },
                        },
                      },
                    },
                  }}
                  label={label}
                  {...restProps}
                />
              </LocalizationProvider>
            );
          }}
        />
      </FieldGridContainer>
    );
  }

  return (
    <Controller
      name={name ?? ''}
      control={control}
      render={({ field }) => {
        const { value } = field;
        const dayjsValue = value
          ? typeof value === 'string' || value instanceof Date
            ? dayjs(value)
            : value
          : null;
        const {
          onChange: otherOnChange,
          value: otherValue,
          ...restProps
        } = otherProps;

        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              data-name={id}
              data-testid={`date-field-${id}`}
              value={dayjsValue}
              onChange={(date: Dayjs | null) => {
                field.onChange(date ? date.format('YYYY-MM-DD') : '');
              }}
              slotProps={{
                textField: {
                  helperText:
                    errorMessage ??
                    (helperText
                      ? `${helperText} [MM/DD/YYYY]`
                      : '[MM/DD/YYYY]'),
                  id,
                  error: !!errorMessage,
                  size: 'small',
                  sx: {
                    '& .MuiInputBase-root': {
                      minHeight: {
                        xs: '44px',
                        sm: '40px',
                      },
                      fontSize: {
                        xs: '16px', // Prevents zoom on iOS
                        sm: '14px',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: {
                        xs: '0.875rem',
                        sm: '1rem',
                      },
                    },
                  },
                },
              }}
              label={label}
              sx={{ width: '100%' }}
              {...restProps}
            />
          </LocalizationProvider>
        );
      }}
    />
  );
};

export default ControlledDateField;
